"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "next/navigation";
import { useEffect, useMemo } from "react";
import { HiivePageClient } from "@/features/hiives/components/hiive-detail-hub/hiive-page";
import { hiivesKeys } from "@/features/hiives/queries/keys";
import { HIIVE_DETAIL_LOAD_ERROR_MESSAGE } from "./hiive-detail-section.shared";
import type { HiiveDetailPageData } from "./hiive-detail-section.types";

type HiiveDetailSectionClientProps = {
  initialData: HiiveDetailPageData;
};

export function HiiveDetailSectionClient({
  initialData,
}: HiiveDetailSectionClientProps) {
  const params = useParams<{ hiiveSlug: string }>();
  const hiiveSlug = params.hiiveSlug ?? "";
  const hasHiiveSlug = hiiveSlug.length > 0;
  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () =>
      hasHiiveSlug
        ? hiivesKeys.detailPage(hiiveSlug)
        : [...hiivesKeys.detailAll(), "page", "invalid"],
    [hasHiiveSlug, hiiveSlug],
  );

  // A location refresh must replace the infinitely fresh detail payload too.
  useEffect(() => {
    queryClient.setQueryData(queryKey, initialData);
  }, [queryClient, queryKey, initialData]);

  const hiiveDetailQuery = useQuery<HiiveDetailPageData>({
    queryKey,
    queryFn: async () => initialData,
    initialData,
    staleTime: Number.POSITIVE_INFINITY,
    enabled: hasHiiveSlug,
  });

  const hiiveDetail = hiiveDetailQuery.data ?? initialData;

  if (!hasHiiveSlug || !hiiveDetail || hiiveDetailQuery.error) {
    return (
      <div className="flex h-dvh flex-col items-center justify-center bg-background px-4 text-center">
        <p className="text-sm text-text-secondary">
          {HIIVE_DETAIL_LOAD_ERROR_MESSAGE}
        </p>
      </div>
    );
  }

  return (
    <HiivePageClient
      hiive={hiiveDetail.hiive}
      initialViewerLocation={hiiveDetail.initialViewerLocation}
      bannerMedia={hiiveDetail.bannerMedia}
      hiiveSlug={hiiveDetail.hiiveSlug}
      membershipState={hiiveDetail.membershipState}
      viewer={hiiveDetail.viewer}
      isAuthenticated={hiiveDetail.isAuthenticated}
      initialFeed={hiiveDetail.initialFeed}
      initialEvents={hiiveDetail.initialEvents}
      initialMembers={hiiveDetail.initialMembers}
      initialVenues={hiiveDetail.initialVenues}
      initialBookableNow={hiiveDetail.initialBookableNow}
      initialShopItems={hiiveDetail.initialShopItems}
      packs={hiiveDetail.packs}
      storyRingStatus={hiiveDetail.storyRingStatus}
      reviewSummary={hiiveDetail.reviewSummary}
    />
  );
}
